/**
 * Shared API utilities for authenticated requests to the Cloudflare Worker
 */

import { supabase } from './supabase'
import { addApiBreadcrumb } from './sentry/breadcrumbs'

// Get the API base URL from environment
const API_BASE = import.meta.env.VITE_AI_WORKER_URL || ''

/**
 * Get the current user's auth token from Supabase session
 */
export async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || null
}

/**
 * Force-refresh the Supabase session and return a fresh token + userId.
 * Falls back to the cached session if the refresh fails (e.g. brief network blip
 * where the existing token may still be valid).
 *
 * Throws if no valid session can be obtained at all.
 */
export async function getFreshToken(): Promise<{ token: string; userId: string }> {
  // Try an explicit refresh first — this guarantees a non-expired token
  const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession()
  if (!refreshError && refreshed.session?.access_token && refreshed.session.user?.id) {
    return { token: refreshed.session.access_token, userId: refreshed.session.user.id }
  }

  // Refresh failed (network blip, etc.) — fall back to cached session which
  // may still be valid if the token hasn't actually expired yet
  const { data: { session } } = await supabase.auth.getSession()
  if (session?.access_token && session.user?.id) {
    return { token: session.access_token, userId: session.user.id }
  }

  throw new Error('Not authenticated')
}

/**
 * Get auth headers for API requests
 * Returns empty object if not authenticated
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuthToken()
  if (!token) {
    return {}
  }
  return {
    'Authorization': `Bearer ${token}`,
  }
}

/**
 * Make an authenticated JSON request to the worker API
 */
export async function authenticatedFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ data: T | null; error: string | null }> {
  const token = await getAuthToken()
  if (!token) {
    return { data: null, error: 'Not authenticated' }
  }

  const method = (options.method || 'GET').toUpperCase()
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })

    // Add breadcrumb with response status
    addApiBreadcrumb(method, endpoint, response.status, response.ok ? 'info' : 'error')

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        data: null,
        error: (errorData as { error?: string }).error || `Request failed: ${response.status}`
      }
    }

    const data = await response.json() as T
    return { data, error: null }
  } catch (err) {
    // Add error breadcrumb
    addApiBreadcrumb(method, endpoint, undefined, 'error')
    const message = err instanceof Error ? err.message : 'Request failed'
    return { data: null, error: message }
  }
}

/**
 * Resolve API endpoint with base URL
 */
export function resolveEndpoint(path: string): string {
  if (!API_BASE) return path
  const cleanBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE
  return `${cleanBase}${path}`
}
